import { useState, useEffect, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { parseTurkishNumber, formatTurkishCurrency } from "@/utils/numberUtils";
import {
  CreditCard,
  Plus,
  UserSearch,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { API } from "@/App";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useTranslation } from "react-i18next";

export default function TrainerPaymentsModule({ user }) {
  const { t } = useTranslation(["common", "dashboard", "members", "trainers", "reservations", "settings", "payments", "rooms", "messaging", "business", "memberDashboard", "packages"]);
  const { formatPrice, currencySymbol } = useCurrency();

  const [trainerId, setTrainerId] = useState(null);
  const [businessId, setBusinessId] = useState(null);
  const [trainerInfo, setTrainerInfo] = useState(null);
  const [students, setStudents] = useState([]);
  const [memberPackages, setMemberPackages] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  
  // Selected member and their packages with debt
  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedMemberPackages, setSelectedMemberPackages] = useState([]);
  
  // Payment form
  const [selectedPackageId, setSelectedPackageId] = useState("");
  const [selectedPackageDebt, setSelectedPackageDebt] = useState(0);
  const [amount, setAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (user?.user_id) {
      fetchTrainerInfo();
    }
  }, [user]);

  const fetchTrainerInfo = async () => {
    try {
      const trainerRes = await fetch(`${API}/trainers/by-user/${user.user_id}`, {
        credentials: "include",
      });
      
      if (trainerRes.ok) {
        const trainers = await trainerRes.json();
        if (trainers.length > 0) {
          const trainer = trainers[0];
          setTrainerId(trainer.trainer_id);
          setBusinessId(trainer.business_id);
          setTrainerInfo(trainer);
          
          // Fetch students and their packages
          await fetchStudentsAndPackages(trainer.trainer_id);
        }
      }
    } catch (error) {
      console.error("Error fetching trainer info:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStudentsAndPackages = async (tId) => {
    try {
      // Get students assigned to this trainer
      const studentsRes = await fetch(`${API}/trainers/${tId}/students`, {
        credentials: "include",
      });
      
      if (studentsRes.ok) {
        const studentsData = await studentsRes.json();
        setStudents(studentsData);
        
        // Fetch all packages and payments for each student
        const allPackages = [];
        const allPayments = [];
        
        for (const student of studentsData) {
          try {
            const pkgRes = await fetch(`${API}/members/${student.member_id}/packages`, {
              credentials: "include",
            });
            if (pkgRes.ok) {
              const pkgs = await pkgRes.json();
              // Only add packages assigned to this trainer
              pkgs.forEach(pkg => {
                if (pkg.trainer_id === tId) {
                  const totalPrice = pkg.price || pkg.sale_price || 0;
                  const paidAmount = pkg.paid_amount || 0;
                  const debt = totalPrice - paidAmount;
                  
                  allPackages.push({ 
                    ...pkg, 
                    member: student,
                    debt: debt
                  });
                  
                  // Fetch payments for ALL packages (not just those with debt)
                  fetchPackagePayments(pkg.member_package_id, student, pkg, allPayments);
                }
              });
            }
          } catch (e) {
            console.error("Error fetching member packages:", e);
          }
        }
        
        setMemberPackages(allPackages);
      }
    } catch (error) {
      console.error("Error fetching students:", error);
    }
  };

  const fetchPackagePayments = async (mpId, member, pkg, allPayments) => {
    try {
      const payRes = await fetch(`${API}/member-packages/${mpId}/payments`, {
        credentials: "include",
      });
      if (payRes.ok) {
        const payData = await payRes.json();
        const pays = Array.isArray(payData) ? payData : (payData.payments || []);
        pays.forEach(pay => {
          allPayments.push({ 
            ...pay, 
            member, 
            package: pkg,
            payment_date: pay.payment_date || pay.created_at
          });
        });
        setPayments([...allPayments]);
      }
    } catch (e) {
      console.error("Error fetching payments:", e);
    }
  };

  const handleMemberSelect = (memberId) => {
    const member = students.find(s => s.member_id === memberId);
    setSelectedMember(member);
    
    // Get ALL packages for this member (not just those with debt)
    const memberPkgs = memberPackages.filter(
      mp => mp.member?.member_id === memberId
    );
    setSelectedMemberPackages(memberPkgs);
    setSelectedPackageId("");
    setAmount("");
    setSelectedPackageDebt(0);
  };

  const handlePackageSelect = (packageId) => {
    setSelectedPackageId(packageId);
    const pkg = selectedMemberPackages.find(p => p.member_package_id === packageId);
    if (pkg) {
      const debt = pkg.debt || 0;
      setSelectedPackageDebt(debt);
      setAmount(debt.toString()); // Auto-fill with remaining debt
    }
  };

  const handleAmountChange = (value) => {
    const numValue = parseTurkishNumber(value);
    
    // Check if amount exceeds remaining debt
    if (numValue > selectedPackageDebt) {
      toast.warning(`Kalan borç ${formatTurkishCurrency(selectedPackageDebt)}. Bu tutardan fazla ödeme alamazsınız.`);
      setAmount(selectedPackageDebt.toString());
      return;
    }
    
    setAmount(value);
  };

  const handleAddPayment = async () => {
    if (!selectedPackageId || !amount) {
      toast.error(t("packages:toast_paketVeTutarSecmelisiniz"));
      return;
    }

    const numAmount = parseTurkishNumber(amount);
    
    // Final validation - cannot exceed debt
    if (numAmount > selectedPackageDebt) {
      toast.error(`Kalan borç ${formatTurkishCurrency(selectedPackageDebt)}. Bu tutardan fazla ödeme alamazsınız.`);
      return;
    }

    if (numAmount <= 0) {
      toast.error(t("common:toast_tutar0danBuyukOlmalidir"));
      return;
    }

    try {
      const response = await fetch(`${API}/payments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          member_package_id: selectedPackageId,
          amount: numAmount,
          payment_method: paymentMethod || null,
          notes: notes || null,
        }),
      });

      if (response.ok) {
        toast.success(t('payments:paymentSaved'));
        setShowPaymentModal(false);
        resetForm();
        // Refresh data
        if (trainerId) {
          fetchStudentsAndPackages(trainerId);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || t("common:couldNotSavePayment"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    }
  };

  const resetForm = () => {
    setSelectedMember(null);
    setSelectedMemberPackages([]);
    setSelectedPackageId("");
    setSelectedPackageDebt(0);
    setAmount("");
    setPaymentMethod("");
    setNotes("");
  };

  // Get packages with debt
  const packagesWithDebt = memberPackages.filter(mp => (mp.debt || 0) > 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy" />
      </div>
    );
  }

  if (!trainerInfo) {
    return (
      <Card className="border-0 shadow-sm">
        <CardContent className="p-12 text-center">
          <CreditCard className="w-12 h-12 text-grey mx-auto mb-3" />
          <p className="text-grey">{t("common:trainerInfoNotFound")}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="font-heading font-bold text-xl text-navy">{t('payments:receivePayment')}</h2>
          <p className="text-grey text-sm">{t("common:ogrencilerinizdenOdemeTahsilEdin")}</p>
        </div>

        <Button
          className="bg-navy hover:bg-navy/90 rounded-xl"
          onClick={() => setShowPaymentModal(true)}
          disabled={packagesWithDebt.length === 0}
          data-testid="add-payment-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Ödeme Al
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-grey text-sm">{t("common:bekleyenOdemeler")}</p>
                <p className="font-heading font-bold text-2xl text-orange-600">
                  {formatTurkishCurrency(packagesWithDebt.reduce((sum, p) => sum + (p.debt || 0), 0))}
                </p>
              </div>
              <div className="w-12 h-12 bg-orange-50 rounded-xl flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-orange-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-grey text-sm">{t("common:borcluOgrenci")}</p>
                <p className="font-heading font-bold text-2xl text-navy">
                  {new Set(packagesWithDebt.map(p => p.member?.member_id)).size}
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
                <CreditCard className="w-6 h-6 text-blue-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Packages with Debt */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-base font-medium">{t("packages:borcluPaketler")}</CardTitle>
        </CardHeader>
        <CardContent>
          {packagesWithDebt.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="whitespace-nowrap">Öğrenci</TableHead>
                    <TableHead className="whitespace-nowrap">{t("packages:package")}</TableHead>
                    <TableHead className="whitespace-nowrap">{t('common:total')}</TableHead>
                    <TableHead className="whitespace-nowrap">{t("common:paidAmount")}</TableHead>
                    <TableHead className="whitespace-nowrap">{t('packages:remaining')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {packagesWithDebt.map((mp) => (
                    <TableRow key={mp.member_package_id}>
                      <TableCell className="whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <Avatar className="h-8 w-8 flex-shrink-0">
                            <AvatarFallback className="bg-navy text-white text-xs">
                              {mp.member?.name?.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          <span>{mp.member?.name} {mp.member?.surname}</span>
                        </div>
                      </TableCell>
                      <TableCell className="whitespace-nowrap">{mp.package_name}</TableCell>
                      <TableCell className="whitespace-nowrap">{currencySymbol}{(mp.price || 0)}</TableCell>
                      <TableCell className="text-green-600 whitespace-nowrap">
                        {currencySymbol}{(mp.paid_amount || 0)}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        <Badge variant="destructive">
                          {currencySymbol}{(mp.debt || 0)}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-8 text-grey">
              <CreditCard className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p>{t("packages:borcluPaketBulunmuyor")}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Payment Modal */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('payments:receivePayment')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Member Selection */}
            <div>
              <Label>Öğrenci *</Label>
              <Select 
                value={selectedMember?.member_id || ""} 
                onValueChange={handleMemberSelect}
              >
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder={t("common:ph_ogrenciSecin")} />
                </SelectTrigger>
                <SelectContent>
                  {students.filter(s => 
                    memberPackages.some(mp => mp.member?.member_id === s.member_id && (mp.debt || 0) > 0)
                  ).map((student) => (
                    <SelectItem key={student.member_id} value={student.member_id}>
                      {student.name} {student.surname}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Package Selection */}
            {selectedMember && (
              <div>
                <Label>Paket *</Label>
                <Select value={selectedPackageId} onValueChange={handlePackageSelect}>
                  <SelectTrigger className="rounded-xl">
                    <SelectValue placeholder={t("packages:ph_paketSecin")} />
                  </SelectTrigger>
                  <SelectContent>
                    {selectedMemberPackages.map((mp) => (
                      <SelectItem key={mp.member_package_id} value={mp.member_package_id}>
                        <div className="flex items-center gap-2">
                          <span>{mp.package_name}</span>
                          <Badge variant="destructive" className="text-xs">
                            {currencySymbol}{(mp.debt || 0)} kaldı
                          </Badge>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Selected Package Info */}
            {selectedPackageId && (
              <div className="p-3 bg-slate-50 rounded-xl text-sm">
                <p className="text-grey">Kalan Borç:</p>
                <p className="font-bold text-red-600 text-lg">
                  {currencySymbol}{selectedPackageDebt}
                </p>
              </div>
            )}

            {/* Amount */}
            <div>
              <Label>Tutar ({currencySymbol}) *</Label>
              <Input
                type="number"
                placeholder="0.00"
                value={amount}
                onChange={(e) => handleAmountChange(e.target.value)}
                max={selectedPackageDebt}
                className="rounded-xl"
                data-testid="payment-amount-input"
              />
              {selectedPackageDebt > 0 && parseTurkishNumber(amount) > selectedPackageDebt && (
                <p className="text-xs text-red-500 mt-1">
                  Kalan borçtan fazla tutar giremezsiniz!
                </p>
              )}
            </div>

            {/* Payment Method */}
            <div>
              <Label>{t('payments:paymentMethod')}</Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder={t("common:ph_secin")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">{t('payments:cash')}</SelectItem>
                  <SelectItem value="credit_card">{t('payments:creditCard')}</SelectItem>
                  <SelectItem value="bank_transfer">{t('payments:bankTransfer')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Notes */}
            <div>
              <Label>Notlar</Label>
              <Textarea
                placeholder={t("common:ph_odemeIleIlgiliNotlar")}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="rounded-xl"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                className="flex-1 rounded-xl"
                onClick={() => {
                  setShowPaymentModal(false);
                  resetForm();
                }}
              >
                İptal
              </Button>
              <Button
                className="flex-1 bg-navy hover:bg-navy/90 rounded-xl"
                onClick={handleAddPayment}
                disabled={!selectedPackageId || !amount || parseTurkishNumber(amount) > selectedPackageDebt}
                data-testid="save-payment-btn"
              >
                Ödemeyi Kaydet
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
